// src/pages/Dashboard.js
//
// REESCRITO — ya no lee de ../data/courses (estático) ni de
// enrolledCourses del progressStore viejo. Ahora carga los cursos
// reales del usuario vía meService.getMisCursos() (trae progreso y
// calificación final ya calculados por el backend) y, para la pestaña
// de actividades pendientes, el detalle de progreso por contenido vía
// aprendizajeService.getProgreso(enrollmentId).

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  MenuBook,
  EmojiEvents,
  BarChart,
  PlayArrow,
  CheckCircle,
  Assignment,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import { meService, aprendizajeService } from '../services/api';

function Dashboard() {
  const [tab, setTab] = useState(0);
  const { user } = useAuth();

  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pendingItems, setPendingItems] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);

  const loadCourses = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await meService.getMisCursos();
      setEnrolledCourses(list);
    } catch (e) {
      setLoadError(e?.message || 'No se pudieron cargar tus cursos.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCourses(); }, [loadCourses]);

  // Cargar actividades pendientes solo cuando se abre esa pestaña
  // (evita N llamadas innecesarias si el usuario nunca la visita).
  useEffect(() => {
    if (tab !== 2 || enrolledCourses.length === 0) return;
    (async () => {
      setLoadingPending(true);
      const results = await Promise.all(
        enrolledCourses.map(async (c) => {
          const detail = await aprendizajeService.getProgreso(c.enrollmentId);
          if (!detail) return [];
          return (detail.contents || [])
            .filter((ct) => !ct.completed && ['Quiz', 'Examen', 'Actividad'].includes(ct.type))
            .map((ct) => ({ ...ct, courseTitle: c.title, courseSlug: c.slug }));
        })
      );
      setPendingItems(results.flat());
      setLoadingPending(false);
    })();
  }, [tab, enrolledCourses]);

  const stats = {
    coursesEnrolled: enrolledCourses.length,
    avgProgress: enrolledCourses.length > 0
      ? Math.round(enrolledCourses.reduce((s, c) => s + (c.progress || 0), 0) / enrolledCourses.length)
      : 0,
    coursesCompleted: enrolledCourses.filter((c) => (c.progress || 0) >= 100).length,
  };

  const statCards = [
    { label: 'Cursos Inscritos', value: stats.coursesEnrolled, icon: MenuBook, color: '#e94560' },
    { label: 'Progreso Promedio', value: `${stats.avgProgress}%`, icon: BarChart, color: '#ffc947' },
    { label: 'Cursos Completados', value: stats.coursesCompleted, icon: CheckCircle, color: '#00d9a5' },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Hola, {user?.name?.split(' ')[0] || 'Estudiante'} 👋
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Bienvenido a tu panel de estudiante
      </Typography>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {statCards.map((stat, i) => (
          <Grid item xs={12} sm={4} key={i}>
            <Card>
              <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                  <Typography variant="h4" fontWeight={700}>{stat.value}</Typography>
                </Box>
                <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: `${stat.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <stat.icon sx={{ color: stat.color }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Mis Cursos" />
        <Tab label="Calificaciones" />
        <Tab label="Actividades Pendientes" />
      </Tabs>

      {/* Mis Cursos Tab */}
      {tab === 0 && (
        <>
          {loading ? (
            <Grid container spacing={3}>
              {[1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rounded" height={220} />
                </Grid>
              ))}
            </Grid>
          ) : enrolledCourses.length > 0 ? (
            <Grid container spacing={3}>
              {enrolledCourses.map((course) => (
                <Grid item xs={12} sm={6} md={4} key={course.enrollmentId}>
                  <Card>
                    <CardMedia
                      component="img"
                      height="120"
                      image={course.image || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800'}
                      alt={course.title}
                    />
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight={600} noWrap gutterBottom>
                        {course.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {course.instructor}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                          <CircularProgress variant="determinate" value={course.progress || 0} size={56} color="success" />
                          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption" fontWeight={700}>{Math.round(course.progress || 0)}%</Typography>
                          </Box>
                        </Box>
                        {course.finalGrade != null && (
                          <Chip label={`Nota final: ${course.finalGrade}`} size="small" color="success" variant="outlined" />
                        )}
                      </Box>

                      <Button
                        component={Link}
                        to={`/learn/${course.slug}`}
                        variant="contained"
                        fullWidth
                        startIcon={<PlayArrow />}
                      >
                        Continuar
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <MenuBook sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom>No tienes cursos aún</Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Explora nuestro catálogo y comienza a aprender
              </Typography>
              <Button component={Link} to="/courses" variant="contained">
                Ver cursos
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Calificaciones Tab */}
      {tab === 1 && (
        <>
          {loading ? (
            <Skeleton variant="rounded" height={200} />
          ) : enrolledCourses.length > 0 ? (
            <Paper sx={{ overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Curso</TableCell>
                    <TableCell>Instructor</TableCell>
                    <TableCell align="center">Progreso</TableCell>
                    <TableCell align="center">Calificación final</TableCell>
                    <TableCell align="center">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {enrolledCourses.map((course) => (
                    <TableRow key={course.enrollmentId}>
                      <TableCell>{course.title}</TableCell>
                      <TableCell>{course.instructor}</TableCell>
                      <TableCell align="center">{Math.round(course.progress || 0)}%</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        {course.finalGrade != null ? course.finalGrade : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={(course.progress || 0) >= 100 ? 'Completado' : 'En curso'}
                          size="small"
                          color={(course.progress || 0) >= 100 ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <BarChart sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Sin calificaciones</Typography>
              <Typography color="text.secondary">
                Inscríbete a un curso para ver tus calificaciones
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Actividades Pendientes Tab */}
      {tab === 2 && (
        <>
          {loadingPending ? (
            <Grid container spacing={2}>
              {[1, 2, 3].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rounded" height={160} />
                </Grid>
              ))}
            </Grid>
          ) : pendingItems.length > 0 ? (
            <Grid container spacing={2}>
              {pendingItems.map((item) => (
                <Grid item xs={12} sm={6} md={4} key={`${item.courseSlug}-${item.contentId}`}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <Assignment color="warning" />
                        <Chip
                          label={item.type}
                          size="small"
                          color={item.type === 'Examen' ? 'error' : 'warning'}
                        />
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {item.courseTitle}
                      </Typography>
                      <Button
                        component={Link}
                        to={`/learn/${item.courseSlug}`}
                        variant="outlined"
                        fullWidth
                        size="small"
                      >
                        Ir a la actividad
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : enrolledCourses.length > 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <EmojiEvents sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>¡Todo al día!</Typography>
              <Typography color="text.secondary">
                No tienes actividades pendientes
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Assignment sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Sin actividades</Typography>
              <Typography color="text.secondary">
                Inscríbete a un curso para ver tus actividades
              </Typography>
            </Box>
          )}
        </>
      )}
    </Container>
  );
}

export default Dashboard;
